import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { apiFetchJson } from "@/utils/apiFetch";
import type { InvestigatorProfile } from "@/views/TrialPlanning/components/InvestigatorProfileDrawer";
import { resolvePlannedSite } from "@/views/TrialPlanning/plannedSites";
import type { PlannedSite } from "@/views/TrialPlanning/types";

function fetchInvestigators(): Promise<InvestigatorProfile[]> {
  return apiFetchJson<InvestigatorProfile[]>(
    `${import.meta.env.VITE_API_URL}/data/portfolio/portfolio-investigators/`,
  );
}

/** The portfolio investigators, plus a by-key index for plan line items (a
 *  planned site is keyed on the investigator chosen for it). */
export function useInvestigatorDirectory() {
  const { data: investigators = [], isLoading } = useQuery({
    queryKey: ["portfolio-investigators"],
    queryFn: fetchInvestigators,
    staleTime: 5 * 60 * 1000,
  });

  const byKey = useMemo(
    () => new Map(investigators.map((inv) => [inv.inv_key, inv])),
    [investigators],
  );

  return { investigators, byKey, isLoading };
}

/** A plan's line items with their site name, profile key and location taken
 *  from the live investigator directory. */
export function usePlannedSites(sites: PlannedSite[]): PlannedSite[] {
  const { byKey } = useInvestigatorDirectory();

  return useMemo(
    () =>
      sites.map((site) => resolvePlannedSite(site, byKey.get(site.site_key))),
    [sites, byKey],
  );
}
